import { Ban, CalendarClock, CheckCircle2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { LeaveTypeModal } from '../components/LeaveTypeModal';
import { leaveTypeApi } from '../leaveApi';
import {
  LEAVE_CREDIT_KIND_LABELS,
  leaveCreditShareLabel,
  LeaveCreditKind,
  type LeaveType,
} from '../leaveTypes';

type TabKey = 'info';

/**
 * One leave type.
 *
 * On the **header** layout rather than the sidebar one: a catalog row reads as a single
 * continuous thing — a name, how it is tracked, a rate, and the trail of who changed it — not
 * a record whose content splits into panels a reader chooses between.
 *
 * The partner earns its keep here: the count of ledger entries already filed against the type
 * is what a reader wants before deciding whether to rename or delete the row.
 */
export function LeaveTypeProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [type, setType] = useState<LeaveType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      leaveTypeApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) setType(next);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this leave type.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  // A plain string is a segment that goes nowhere; the two links are the way back.
  const breadcrumb: BreadcrumbSegment[] = [
    'Human Resources',
    'Personnel',
    { label: 'Settings', to: '/personnel/settings' },
    { label: 'Leave Types', to: '/personnel/settings/leave-types' },
    type?.name ?? 'Leave type',
  ];

  if (error) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading || !type) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const isLedger = type.creditKind === LeaveCreditKind.Ledger;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="leave-type-profile"
        header={
          <ProfileHeader
            id="leave-type-profile-header"
            icon={<CalendarClock className="h-6 w-6" aria-hidden="true" />}
            title={type.name}
            meta={[
              type.code,
              LEAVE_CREDIT_KIND_LABELS[type.creditKind],
              // Unlabelled, because "12 entries" says what it is on its own.
              `${type.recordCount} ${type.recordCount === 1 ? 'entry' : 'entries'}`,
            ]}
            status={
              <StatusText tone={type.isActive ? 'active' : 'inactive'}>
                {type.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={type.createdByName}
            createdAt={type.createdAt}
          />
        }
        banner={
          type.isActive ? (
            <ProfileBanner
              id="leave-type-profile-banner"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message={
                isLedger
                  ? 'This type is granted: it carries a running balance on every employee, and appears on their Credit Balance.'
                  : 'This type is granted as a whole entitlement. It appears on every employee, and keeps no running balance to draw down.'
              }
            />
          ) : (
            <ProfileBanner
              id="leave-type-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This leave type is deleted. It is no longer listed on an employee's Credit Balance, and the entries already filed against it are kept."
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="leave-type-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'leave-types.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Name', value: type.name },
              { label: 'Code', value: type.code },
              { label: 'Parent type', value: type.parentLeaveTypeName },
              { label: 'Credit', value: leaveCreditShareLabel(type) },
              { label: 'Tracked as', value: LEAVE_CREDIT_KIND_LABELS[type.creditKind] },
              {
                label: 'Monthly accrual',
                /*
                  An em dash where there is no rate, never 0.00. A type with no rate does not
                  accrue by the calendar at all — Compensatory Leave is earned from rendered
                  overtime — which is a different fact from accruing none each month.
                */
                value:
                  type.monthlyAccrualDays == null ? null : (
                    <span className="font-mono tabular-nums">
                      {type.monthlyAccrualDays.toFixed(2)}
                    </span>
                  ),
              },
              { label: 'Description', value: type.description },
              {
                label: 'Order',
                value: <span className="font-mono tabular-nums">{type.sortOrder}</span>,
              },
              {
                label: 'Entries filed',
                value: <span className="font-mono tabular-nums">{type.recordCount}</span>,
              },
            ]}
          />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="leave-type-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: type.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(type.createdAt)} className="font-mono">
                    {formatDateTime(type.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: type.updatedByName },
              {
                label: 'Updated',
                value: type.updatedAt ? (
                  <time dateTime={toIso(type.updatedAt)} className="font-mono">
                    {formatDateTime(type.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <LeaveTypeModal
          open
          editing={type}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
